import { z } from 'zod';

export const signInSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});
export type SignInInput = z.infer<typeof signInSchema>;

export const signUpSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Enter a valid email'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });
export type SignUpInput = z.infer<typeof signUpSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().email('Enter a valid email'),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const rideRequestSchema = z.object({
  pickupLocation: z.string().min(1, 'Pickup location is required'),
  pickupLat: z.number().min(-90).max(90),
  pickupLng: z.number().min(-180).max(180),
  destination: z.string().min(1, 'Destination is required'),
  destinationLat: z.number().min(-90).max(90),
  destinationLng: z.number().min(-180).max(180),
  rideType: z.enum([
    'economy',
    'comfort',
    'premium',
    'electric',
    'standard',
    'van',
  ]),
  scheduledTime: z.string().datetime().optional(),
  routeDistanceMeters: z.number().int().nonnegative().optional(),
  routeDurationSeconds: z.number().int().nonnegative().optional(),
  routePolyline: z.string().optional(),
  estimatedMarketRate: z.number().nonnegative().optional(),
});
export type RideRequestInput = z.infer<typeof rideRequestSchema>;

export const driverSignupSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phoneNumber: z.string().min(7, 'Enter a valid phone number'),
  licenseNumber: z.string().min(1, 'License number is required'),
  vehicleMake: z.string().min(1, 'Make is required'),
  vehicleModel: z.string().min(1, 'Model is required'),
  vehicleYear: z
    .number()
    .int()
    .min(1990, 'Year must be 1990 or later')
    .max(new Date().getFullYear() + 1),
  vehicleColor: z.string().min(1, 'Color is required'),
  licensePlate: z.string().min(1, 'License plate is required'),
});
export type DriverSignupInput = z.infer<typeof driverSignupSchema>;

/**
 * The gate `useRideCreation` applies before calling `create_ride`.
 *
 * `create_ride` validates too — it rejects a null coordinate outright — but a
 * server error is a poor way to learn that a form was incomplete, and the RPC's
 * message ("create_ride: pickup/destination coordinates required") is written
 * for an operator, not a rider. This catches the same class client-side with
 * copy a person can act on, and stays the *second* line of defence: the RPC
 * remains authoritative and is never assumed to have been fronted by this.
 *
 * Coordinates are validated for real range, not merely for presence. A NaN or
 * an out-of-range pair reaches PostGIS as a malformed point rather than as a
 * clean rejection.
 */
export const createRideInputSchema = z.object({
  pickupLocation: z.string().trim().min(1, 'Enter a pickup location'),
  pickupCoords: z.tuple([
    z.number().finite().min(-180).max(180),
    z.number().finite().min(-90).max(90),
  ]),
  destination: z.string().trim().min(1, 'Enter a destination'),
  destinationCoords: z.tuple([
    z.number().finite().min(-180).max(180),
    z.number().finite().min(-90).max(90),
  ]),
  routeDistanceMeters: z.number().int().nonnegative().optional(),
  routeDurationSeconds: z.number().int().nonnegative().optional(),
  routePolyline: z.string().optional(),
  estimatedMarketRate: z.number().nonnegative().optional(),
  vehicleTypePreference: z.string().optional(),
  scheduledTime: z.date().optional(),
});
export type CreateRideSchemaInput = z.infer<typeof createRideInputSchema>;

